/**
 * "Interpret as": two adjacent 16-bit words of an S7 / MELSEC block scan read
 * as one 32-bit value (float32, int32, uint32), with the decoded preview.
 *
 * A block scan lists raw words (`DB1.DBW0  0x42A0`, `DB1.DBW2  0x1EB8`), each
 * as an int16, while the PLC often holds a REAL across both
 * (0x42A01EB8 = 80.06). This finds such pairs, decodes them and turns the
 * chosen reading into a point at the first word's address with that type,
 * which is how the drivers read a 32-bit value:
 *
 * - S7: big-endian, the first word is the high one; the address is the first
 *   word's `{area, db, byte}` (the driver reads 4 bytes from there).
 * - MELSEC: the first device holds the low word (the driver's default word
 *   order, "little"); the address is the first device, e.g. `{device: "D100"}`.
 *
 * Pure, so it is unit-tested.
 */

import type { PointDataType } from "@/lib/api/things";
import { DRIVER } from "./drivers";
import { addressKey, metricFromName, type PickedPoint } from "./points";

export type Word32Type = "float32" | "int32" | "uint32";
export const WORD32_TYPES: Word32Type[] = ["float32", "int32", "uint32"];

/** The raw 16-bit word a block-scan point shows: the hex in its name, else its value. Null when neither. */
export function wordOf(p: Pick<PickedPoint, "label" | "value">): number | null {
  const hex = /0x([0-9A-Fa-f]{1,4})\s*$/.exec(p.label ?? "");
  if (hex) return parseInt(hex[1], 16);
  const v = p.value;
  if (typeof v === "number" && Number.isInteger(v) && v >= -32768 && v <= 65535) return v & 0xffff;
  return null;
}

/** Two words as an unsigned 32-bit number, `hi` first. */
export function combineWords(hi: number, lo: number): number {
  return ((hi & 0xffff) * 0x10000 + (lo & 0xffff)) >>> 0;
}

/** A 32-bit pattern read as the given type. */
export function decode32(u32: number, type: Word32Type): number {
  if (type === "uint32") return u32 >>> 0;
  if (type === "int32") return u32 | 0;
  const view = new DataView(new ArrayBuffer(4));
  view.setUint32(0, u32 >>> 0);
  return view.getFloat32(0);
}

/** A decoded value for display: floats to 4 significant decimals, "NaN" / "∞" spelled out. */
export function decodedText(v: number): string {
  if (Number.isNaN(v)) return "NaN";
  if (!Number.isFinite(v)) return v > 0 ? "∞" : "-∞";
  if (Number.isInteger(v)) return String(v);
  const abs = Math.abs(v);
  if (abs !== 0 && (abs < 1e-4 || abs >= 1e9)) return v.toExponential(3);
  return v.toFixed(4).replace(/\.?0+$/, "");
}

/** Two adjacent words that can be read as one 32-bit value. */
export interface WordPair {
  /** Key of the first word (the 32-bit point's address is the first word's). */
  firstKey: string;
  /** Key of the second word (covered by the 32-bit point). */
  secondKey: string;
  /** The 32-bit pattern in the driver's word order. */
  u32: number;
  address: Record<string, unknown>;
  /** "DB1.DBD0", "MD8", "D100–D101". */
  label: string;
}

const MELSEC_WORD_DEVICES = new Set(["D", "W", "R", "ZR", "SD", "SW", "TN", "CN"]);
const MELSEC_HEX_WORD = new Set(["W", "SW"]);

function melsecSplit(device: string): { dev: string; n: number; hex: boolean } | null {
  const m = /^([A-Za-z]{1,2})([0-9A-Fa-f]+)$/.exec(device.trim());
  if (!m) return null;
  let dev = m[1].toUpperCase();
  let digits = m[2];
  // A one-letter device followed by hex digits (W1F) is ambiguous with a two-letter one; prefer the known word devices.
  if (!MELSEC_WORD_DEVICES.has(dev) && MELSEC_WORD_DEVICES.has(dev.slice(0, 1))) {
    digits = dev.slice(1) + digits;
    dev = dev.slice(0, 1);
  }
  if (!MELSEC_WORD_DEVICES.has(dev)) return null;
  const hex = MELSEC_HEX_WORD.has(dev);
  const n = hex ? parseInt(digits, 16) : /^\d+$/.test(digits) ? parseInt(digits, 10) : NaN;
  return Number.isFinite(n) ? { dev, n, hex } : null;
}

const int = (v: unknown): number | undefined => (typeof v === "number" && Number.isInteger(v) ? v : undefined);

/** A 16-bit point (not a bit, not already wider). */
function isWord(p: PickedPoint): boolean {
  return p.data_type === "int16" || p.data_type === "uint16";
}

/**
 * `a` and `b` as one 32-bit value, or null when they are not two adjacent
 * words of the same area / data block / device on an S7 or MELSEC PLC.
 */
export function adjacentWordPair(driver: string, a: PickedPoint, b: PickedPoint): WordPair | null {
  if (!isWord(a) || !isWord(b)) return null;
  const wa = wordOf(a);
  const wb = wordOf(b);
  if (wa === null || wb === null) return null;
  const A = a.address;
  const B = b.address;
  if (driver === DRIVER.S7) {
    if (A.bit !== undefined || B.bit !== undefined) return null;
    if (A.area !== B.area || typeof A.area !== "string") return null;
    if (A.area === "DB" && int(A.db) !== int(B.db)) return null;
    const ba = int(A.byte);
    const bb = int(B.byte);
    if (ba === undefined || bb === undefined || bb !== ba + 2) return null;
    const where = A.area === "DB" ? `DB${int(A.db) ?? "?"}.DBD${ba}` : `${A.area}D${ba}`;
    return { firstKey: addressKey(A), secondKey: addressKey(B), u32: combineWords(wa, wb), address: A, label: where };
  }
  if (driver === DRIVER.MELSEC) {
    if (typeof A.device !== "string" || typeof B.device !== "string") return null;
    const sa = melsecSplit(A.device);
    const sb = melsecSplit(B.device);
    if (!sa || !sb || sa.dev !== sb.dev || sb.n !== sa.n + 1) return null;
    // The low word first (word order "little", the driver's default), unless the address says otherwise.
    const order = typeof A.word_order === "string" ? A.word_order : "little";
    const u32 = order === "big" ? combineWords(wa, wb) : combineWords(wb, wa);
    return { firstKey: addressKey(A), secondKey: addressKey(B), u32, address: A, label: `${A.device}–${B.device}` };
  }
  return null;
}

/** The pair's readings as each 32-bit type (only the ones the driver can decode, when that list is known). */
export function interpretations(
  pair: WordPair,
  decodable?: readonly PointDataType[],
): Array<{ type: Word32Type; value: number; text: string }> {
  return WORD32_TYPES.filter((t) => !decodable || decodable.length === 0 || decodable.includes(t)).map((type) => {
    const value = decode32(pair.u32, type);
    return { type, value, text: decodedText(value) };
  });
}

/** The pair read as `type`, as a point at the first word's address. */
export function pairToPoint(pair: WordPair, type: Word32Type): PickedPoint {
  return {
    key: pair.firstKey,
    metric: metricFromName(pair.label.replace(/–/g, "_")),
    address: pair.address,
    data_type: type,
    label: `${pair.label} (${type})`,
    value: decode32(pair.u32, type),
  };
}

/** Every adjacent pair in a list of block-scan points, keyed by the first point's key. */
export function wordPairs(driver: string, points: PickedPoint[]): Map<string, WordPair> {
  const out = new Map<string, WordPair>();
  for (let i = 0; i + 1 < points.length; i++) {
    const pair = adjacentWordPair(driver, points[i], points[i + 1]);
    if (pair) out.set(points[i].key, pair);
  }
  return out;
}
